import type { Phrase } from './types';
import { expandQuery, type SearchResult } from './search';

type Pending = {
  resolve: (results: SearchResult[]) => void;
  reject: (error: Error) => void;
};

export type SearchClient = {
  search: (query: string) => Promise<SearchResult[]>;
  dispose: () => void;
};

/**
 * Search through a prebuilt index in a worker. If workers or the artifact are
 * unavailable, build locally so search still works after a partial old deploy.
 */
export function createSearchClient(phrases: Phrase[], indexUrl?: string): SearchClient {
  let fallbackIndex: Promise<import('./searchIndex').PhraseSearchIndex> | undefined;
  const fallback = async (query: string): Promise<SearchResult[]> => {
    const module = await import('./searchIndex');
    fallbackIndex ||= Promise.resolve(module.buildSearchIndex(phrases));
    return module.searchIndexResults(await fallbackIndex, query);
  };

  if (!indexUrl || typeof Worker === 'undefined') {
    return { search: fallback, dispose: () => undefined };
  }

  const worker = new Worker('/search-worker.js?v=2');
  const pending = new Map<number, Pending>();
  let requestId = 0;
  let workerFailed = false;
  let settleReady: (ready: boolean) => void = () => undefined;
  const ready = new Promise<boolean>((resolve) => { settleReady = resolve; });

  worker.onmessage = (event) => {
    const message = event.data;
    if (message.type === 'ready') {
      settleReady(true);
      return;
    }
    if (message.type === 'error') {
      workerFailed = true;
      settleReady(false);
      return;
    }
    const request = pending.get(message.requestId);
    if (!request) return;
    pending.delete(message.requestId);
    if (message.type === 'results') request.resolve(message.results);
    else request.reject(new Error(message.message || 'Search worker failed'));
  };
  worker.onerror = () => {
    workerFailed = true;
    settleReady(false);
    for (const request of pending.values()) request.reject(new Error('Search worker failed'));
    pending.clear();
  };
  worker.postMessage({ type: 'init', indexUrl });

  return {
    search: async (query) => {
      if (!(await ready) || workerFailed) return fallback(query);
      const id = ++requestId;
      try {
        return await new Promise<SearchResult[]>((resolve, reject) => {
          pending.set(id, { resolve, reject });
          const expanded = expandQuery(query);
          if (!expanded) {
            pending.delete(id);
            resolve([]);
            return;
          }
          worker.postMessage({
            type: 'search', requestId: id, msQuery: expanded.msQuery, terms: expanded.terms,
          });
        });
      } catch (_) {
        workerFailed = true;
        return fallback(query);
      }
    },
    dispose: () => {
      worker.terminate();
      for (const request of pending.values()) request.reject(new Error('Search client disposed'));
      pending.clear();
    },
  };
}
